import { useState } from 'react';
import {
  getMe,
  patchProfile,
  requestEmailChange,
  type Me,
  type ProfilePatch,
} from '../api/endpoints.js';
import { useAction, useApi } from '../api/hooks.js';
import {
  Async,
  Badge,
  Bool,
  Button,
  Failure,
  Field,
  KeyValue,
  Note,
  Panel,
  Raw,
  Row,
  Screen,
} from '../ui/kit.js';

const EDITABLE = [
  'first_name',
  'last_name',
  'date_of_birth',
  'employer_name',
  'occupation',
  'school_name',
] as const;

type Draft = Record<(typeof EDITABLE)[number], string>;

function draftOf(me: Me): Draft {
  return {
    first_name: me.person.first_name ?? '',
    last_name: me.person.last_name ?? '',
    date_of_birth: me.person.date_of_birth ?? '',
    employer_name: me.person.employer_name ?? '',
    occupation: me.person.occupation ?? '',
    school_name: me.person.school_name ?? '',
  };
}

/**
 * PATCH /v1/me/profile carries ONLY the fields the user actually changed.
 *
 * The endpoint distinguishes three states — key absent (unchanged), null (clear),
 * value (set) — and sending every field every time is precisely the legacy bug
 * it was built to kill: editing the employer wiped the name. So an untouched
 * field is omitted, and a field the user emptied becomes null.
 */
function patchFrom(draft: Draft, me: Me): ProfilePatch {
  const patch: ProfilePatch = {};
  for (const key of EDITABLE) {
    const before = me.person[key] ?? '';
    const after = draft[key];
    if (after === before) continue;
    patch[key] = after.trim().length === 0 ? null : after;
  }
  return patch;
}

export function ProfileScreen() {
  const me = useApi(getMe, []);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [email, setEmail] = useState('');

  const save = useAction(patchProfile, () => me.reload());
  const changeEmail = useAction(requestEmailChange, () => me.reload());

  return (
    <Screen
      title="Profile & email"
      lede="GET /v1/me is an explicit projection — no row is ever spread into a response. Only changed fields are sent on save."
    >
      <Async state={me}>
        {(data) => {
          const current = draft ?? draftOf(data);
          const patch = patchFrom(current, data);
          const changed = Object.keys(patch);
          return (
            <>
              <Panel title="account">
                <KeyValue
                  rows={[
                    ['phone_e164', <code key="p">{data.account.phone_e164}</code>],
                    ['status', <Badge key="s">{data.account.status}</Badge>],
                    ['person_id', <code key="i">{data.person.person_id}</code>],
                  ]}
                />
              </Panel>

              <Panel title="onboarding">
                <KeyValue
                  rows={[
                    ['next_step', <Badge key="n">{data.onboarding.next_step ?? 'none'}</Badge>],
                    ['profile_complete', <Bool key="a" value={data.onboarding.profile_complete} />],
                    ['quiz_completed', <Bool key="b" value={data.onboarding.quiz_completed} />],
                    ['seat_claimed', <Bool key="c" value={data.onboarding.seat_claimed} />],
                    ['consent_signed', <Bool key="d" value={data.onboarding.consent_signed} />],
                    ['enrolled', <Bool key="e" value={data.onboarding.enrolled} />],
                    ['email_verified', <Bool key="f" value={data.onboarding.email_verified} />],
                    ['photos_uploaded', <Bool key="g" value={data.onboarding.photos_uploaded} />],
                    [
                      'notifications_enabled',
                      <Bool key="h" value={data.onboarding.notifications_enabled} />,
                    ],
                    [
                      'blocking_coverage',
                      data.onboarding.blocking_coverage === null
                        ? null
                        : data.onboarding.blocking_coverage.join(', '),
                    ],
                  ]}
                />
              </Panel>

              <Panel title="consent eligibility — why the selfie step will or won't be reachable">
                <KeyValue
                  rows={[
                    ['required_signer_role', data.consent_eligibility.required_signer_role],
                    ['blocked_reason', data.consent_eligibility.blocked_reason],
                  ]}
                />
              </Panel>

              <Panel title="edit profile">
                <div className="row">
                  {EDITABLE.map((key) => (
                    <Field
                      key={key}
                      label={key === 'date_of_birth' ? 'date_of_birth (YYYY-MM-DD)' : key}
                      value={current[key]}
                      onChange={(value) => setDraft({ ...current, [key]: value })}
                      disabled={save.pending}
                    />
                  ))}
                </div>
                <p className="muted" style={{ marginTop: 10 }}>
                  {changed.length === 0
                    ? 'nothing changed — an empty patch is a valid no-op'
                    : `will send: ${JSON.stringify(patch)}`}
                </p>
                <Row>
                  <Button onClick={() => save.run(patch)} disabled={save.pending || changed.length === 0}>
                    {save.pending ? 'saving…' : 'Save changes'}
                  </Button>
                  <Button variant="secondary" onClick={() => setDraft(null)} disabled={save.pending}>
                    Reset
                  </Button>
                </Row>
                <Note>
                  <code>date_of_birth</code> is write-once: it decides self- versus
                  guardian-signature on a legal record, so a second, different value returns{' '}
                  <code>DOB_ALREADY_SET</code> rather than overwriting. Emptying a field sends{' '}
                  <code>null</code>, which clears it.
                </Note>
                {save.error !== null && (
                  <Failure
                    error={save.error}
                    hint={
                      save.error.code === 'DOB_ALREADY_SET'
                        ? 'Changing a set DOB is a support path, not an endpoint.'
                        : undefined
                    }
                  />
                )}
                {save.result !== null && <Raw label="patch response" value={save.result} />}
              </Panel>

              <Panel title="email">
                <KeyValue
                  rows={[
                    ['email', data.person.email],
                    ['email_verified', <Bool key="v" value={data.person.email_verified} />],
                    ['pending_email', data.person.pending_email],
                  ]}
                />
                <Row>
                  <Field label="new email" value={email} onChange={setEmail} type="email" />
                  <Button onClick={() => changeEmail.run(email)} disabled={changeEmail.pending}>
                    {changeEmail.pending ? 'sending…' : 'Send verification'}
                  </Button>
                </Row>
                <Note>
                  202 means accepted, not delivered — the api will not claim a mail is on its way
                  when the provider refused, and answers <code>EMAIL_DELIVERY_FAILED</code> with a
                  502 instead. Dev runs <code>EMAIL_PROVIDER=ses</code>, still inside the SES
                  sandbox, so delivery only reaches addresses verified in the SES console.
                </Note>
                {changeEmail.error !== null && <Failure error={changeEmail.error} />}
                {changeEmail.result !== null && <Raw label="response" value={changeEmail.result} />}
              </Panel>

              <Raw label="GET /v1/me" value={data} />
            </>
          );
        }}
      </Async>
    </Screen>
  );
}
